import { type SaveWashSettings, saveWashSettingsSchema, type WashSettings } from "@station/contracts";
import { Alert, Button, Card, FormField, Input, SkeletonRows, Textarea, useToast } from "@station/ui";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useRef, useState } from "react";
import { type Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useWashPermissions, useWashSettings } from "../../../features/wash/queries.js";
import { WashPage } from "../../../features/wash/shared.js";
import {
  formIssues,
  SMS_PLACEHOLDERS,
  smsPreview,
  smsSegments,
  unknownPlaceholders,
} from "../../../features/wash/wash-values.js";
import { api } from "../../../lib/api.js";
import { applyFieldErrors, errorMessage, isApiError } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";
import { useDraftGuard, useSubmitShortcut } from "../../../lib/hooks.js";
import { queryKeys } from "../../../lib/query.js";

export const Route = createFileRoute("/_app/yikama/ayarlar")({ component: SettingsRoute });

const NUMBER_FIELDS = [
  "codeValidityMinutes",
  "creditValidityDays",
  "codeAttemptLimit",
  "photoRetentionDays",
] as const;
const FIELDS = [...NUMBER_FIELDS, "readySmsTemplate"] as const;

interface SettingsFormValues {
  codeValidityMinutes: string;
  creditValidityDays: string;
  codeAttemptLimit: string;
  photoRetentionDays: string;
  readySmsTemplate: string;
}

/** Form (dizge) → sözleşme girdisi: tam sayı metinleri sayıya çevrilir; çevrilemeyen metin kalır → Zod iletisi alanda. */
function normalizeSettings(values: unknown): unknown {
  if (typeof values !== "object" || values === null) return values;
  const next: Record<string, unknown> = { ...values };
  for (const key of NUMBER_FIELDS) {
    const text = typeof next[key] === "string" ? (next[key] as string).trim() : next[key];
    next[key] = typeof text === "string" && /^\d+$/.test(text) ? Number(text) : text;
  }
  return next;
}

/**
 * Form çözümleyicisi: normalleştirilmiş değer SÖZLEŞME şemasıyla doğrulanır (sunucuyla aynı kural); Zod'un İngilizce
 * varsayılan iletileri Türkçeye çevrilir (`formIssues`).
 */
function settingsResolver(version: number): Resolver<SettingsFormValues, unknown, SaveWashSettings> {
  return async (values) => {
    const body = normalizeSettings({ ...values, version });
    const issues = formIssues(saveWashSettingsSchema, body);
    if (Object.keys(issues).length === 0) return { values: saveWashSettingsSchema.parse(body), errors: {} };
    return {
      values: {},
      errors: Object.fromEntries(
        Object.entries(issues).map(([key, message]) => [key, { type: "validation", message }]),
      ),
    };
  };
}

/**
 * Yıkama ayarları (Prompt 18 web, ADR 0018 §9–13, §20): başlatma kodu ve self-servis kredisinin geçerliliği, kod deneme
 * sınırı, iş emri fotoğraflarının saklama süresi ve işlemsel "aracınız hazır" SMS şablonu (yer tutucu yardımı + canlı
 * önizleme). Kiracı ayarıdır: yazmak KİRACI kapsamlı `wash.manage` ister (tüm istasyonları etkiler).
 */
function SettingsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useWashPermissions();
  const settings = useWashSettings(permissions.enabled && permissions.canRead);
  return (
    <WashPage title={t("menu.washSettings")} description={t("wash.settings.description")}>
      {settings.error ? (
        <Alert tone="danger">{errorMessage(settings.error)}</Alert>
      ) : !settings.data ? (
        <SkeletonRows rows={6} />
      ) : (
        <SettingsForm
          key={`${settings.data.version}`}
          settings={settings.data}
          // Düzenleme hakkı SUNUCUDAN (kiracı kapsamlı `wash.manage`); istemci atama listesinden tahmin etmez.
          canEdit={settings.data.canEdit}
          canManage={permissions.canManage}
        />
      )}
    </WashPage>
  );
}

function SettingsForm({
  settings,
  canEdit,
  canManage,
}: {
  settings: WashSettings;
  canEdit: boolean;
  canManage: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);
  const templateRef = useRef<HTMLTextAreaElement | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<SettingsFormValues, unknown, SaveWashSettings>({
    resolver: settingsResolver(settings.version),
    defaultValues: {
      codeValidityMinutes: String(settings.codeValidityMinutes),
      creditValidityDays: String(settings.creditValidityDays),
      codeAttemptLimit: String(settings.codeAttemptLimit),
      photoRetentionDays: String(settings.photoRetentionDays),
      readySmsTemplate: settings.readySmsTemplate,
    },
  });
  const { errors, isDirty, isSubmitting } = form.formState;
  useDraftGuard(isDirty && !isSubmitting);
  useSubmitShortcut(formRef);
  const template = form.watch("readySmsTemplate");
  const preview = smsPreview(template, {
    plate: "34 ABC 123",
    station: t("wash.settings.sampleStation"),
    orderNo: "YKM2026000012",
  });
  const unknown = unknownPlaceholders(template);

  const submit = form.handleSubmit(async (clean) => {
    setFormError(null);
    try {
      await api.PUT("/wash/settings", { body: clean });
      await queryClient.invalidateQueries({ queryKey: queryKeys.wash("settings") });
      toast({ tone: "success", title: t("wash.settings.saved") });
      form.reset(form.getValues());
    } catch (error) {
      if (isApiError(error, "VERSION_CONFLICT")) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.wash("settings") });
        return setFormError(t("wash.conflict"));
      }
      const before = Object.keys(form.formState.errors).length;
      const unmatched = applyFieldErrors(error, form.setError, FIELDS);
      if (unmatched.length > 0 || Object.keys(form.formState.errors).length === before)
        setFormError(errorMessage(error));
    }
  });

  function insertPlaceholder(token: string): void {
    const element = templateRef.current;
    const current = form.getValues("readySmsTemplate");
    const start = element?.selectionStart ?? current.length;
    const end = element?.selectionEnd ?? current.length;
    form.setValue("readySmsTemplate", `${current.slice(0, start)}${token}${current.slice(end)}`, {
      shouldDirty: true,
      shouldValidate: form.formState.isSubmitted,
    });
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(start + token.length, start + token.length);
    });
  }

  const register = form.register("readySmsTemplate");
  return (
    <form
      ref={formRef}
      className="grid gap-4"
      noValidate
      aria-label={t("wash.settings.formLabel")}
      onSubmit={(event) => void submit(event)}
    >
      {!canEdit ? (
        <Alert tone="info">
          {canManage ? t("wash.settings.tenantScopeOnly") : t("wash.settings.readOnly")}
        </Alert>
      ) : null}
      {settings.isDefault ? <Alert tone="info">{t("wash.settings.defaults")}</Alert> : null}
      {formError ? <Alert tone="danger">{formError}</Alert> : null}
      <Card className="grid gap-4 p-4">
        <h2 className="text-base font-semibold">{t("wash.settings.machineTitle")}</h2>
        <div className="grid items-start gap-4 sm:grid-cols-3">
          <FormField
            label={t("wash.settings.codeValidityMinutes")}
            description={t("wash.settings.codeValidityHint")}
            error={errors.codeValidityMinutes?.message}
            required
          >
            {(control) => (
              <Input
                {...control}
                {...form.register("codeValidityMinutes")}
                inputMode="numeric"
                className="tabular"
                disabled={!canEdit}
              />
            )}
          </FormField>
          <FormField
            label={t("wash.settings.codeAttemptLimit")}
            description={t("wash.settings.codeAttemptHint")}
            error={errors.codeAttemptLimit?.message}
            required
          >
            {(control) => (
              <Input
                {...control}
                {...form.register("codeAttemptLimit")}
                inputMode="numeric"
                className="tabular"
                disabled={!canEdit}
              />
            )}
          </FormField>
          <FormField
            label={t("wash.settings.creditValidityDays")}
            description={t("wash.settings.creditValidityHint")}
            error={errors.creditValidityDays?.message}
            required
          >
            {(control) => (
              <Input
                {...control}
                {...form.register("creditValidityDays")}
                inputMode="numeric"
                className="tabular"
                disabled={!canEdit}
              />
            )}
          </FormField>
        </div>
      </Card>
      <Card className="grid gap-4 p-4">
        <h2 className="text-base font-semibold">{t("wash.settings.handWashTitle")}</h2>
        <FormField
          label={t("wash.settings.photoRetentionDays")}
          description={t("wash.settings.photoRetentionHint")}
          error={errors.photoRetentionDays?.message}
          className="sm:max-w-xs"
          required
        >
          {(control) => (
            <Input
              {...control}
              {...form.register("photoRetentionDays")}
              inputMode="numeric"
              className="tabular"
              disabled={!canEdit}
            />
          )}
        </FormField>
        <FormField
          label={t("wash.settings.readySmsTemplate")}
          description={t("wash.settings.readySmsHint")}
          error={errors.readySmsTemplate?.message}
          required
        >
          {(control) => (
            <Textarea
              {...control}
              {...register}
              ref={(element) => {
                register.ref(element);
                templateRef.current = element;
              }}
              rows={3}
              maxLength={300}
              disabled={!canEdit}
            />
          )}
        </FormField>
        {canEdit ? (
          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            aria-label={t("wash.settings.placeholders")}
          >
            <span className="text-xs text-muted-foreground">{t("wash.settings.placeholders")}:</span>
            {SMS_PLACEHOLDERS.map((token) => (
              <Button
                key={token}
                variant="outline"
                size="sm"
                className="font-mono"
                aria-label={t("wash.settings.insertPlaceholder", {
                  token,
                  meaning: t(`wash.settings.placeholder.${token.slice(1, -1)}`),
                })}
                onClick={() => insertPlaceholder(token)}
              >
                {token}
              </Button>
            ))}
          </div>
        ) : null}
        <dl className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-3">
          {SMS_PLACEHOLDERS.map((token) => (
            <div key={token} className="flex gap-1">
              <dt className="font-mono">{token}</dt>
              <dd>{t(`wash.settings.placeholder.${token.slice(1, -1)}`)}</dd>
            </div>
          ))}
        </dl>
        {unknown.length > 0 ? (
          <Alert tone="warning">
            {t("wash.settings.unknownPlaceholders", { tokens: unknown.join(", ") })}
          </Alert>
        ) : null}
        <div className="grid gap-1.5">
          <span className="text-sm font-medium">{t("wash.settings.preview")}</span>
          <output
            aria-live="polite"
            className="rounded-lg border bg-muted/40 p-3 text-sm whitespace-pre-wrap"
            data-testid="sms-preview"
          >
            {preview}
          </output>
          <span className="text-xs text-muted-foreground">
            {t("wash.settings.previewMeta", { length: preview.length, segments: smsSegments(preview) })}
          </span>
        </div>
      </Card>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {settings.updatedAt
            ? t("wash.settings.updatedAt", { at: formatDateTime(settings.updatedAt) })
            : null}
        </span>
        {canEdit ? (
          <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
            {t("common.save")}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
